import React, { useState, useEffect } from "react";
import { Button, Divider, Form, Input, Select, Upload } from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import defaultPointIcon from "../../assets/carIcon.svg";

const TransportDetailsEditor = ({
  slide,
  updateSlide,
  removeSlide,
  triggerSave,
}) => {
  const defaultTransport = {
    details: {
      vehicleType: "",
      model: "",
      maxPassengers: 0,
      maxLuggage: 0,
      airConditioned: "Yes",
    },
    points: [],
    vehicleImage: "",
  };

  const [transport, setTransport] = useState(
    slide.transportOptions?.[0] || defaultTransport
  );
  const [background, setBackground] = useState(slide.bg || "");
  const [title, setTitle] = useState(slide.title || "TRANSPORTATION");
  const [subtitle, setSubtitle] = useState(
    slide.subtitle || "Not Just A Destination"
  );

  useEffect(() => {
    setTransport(slide.transportOptions?.[0] || defaultTransport);
    setBackground(slide.bg || "");
    setTitle(slide.title || "TRANSPORTATION");
    setSubtitle(slide.subtitle || "Not Just A Destination");
  }, [slide]);

  useEffect(() => {
    updateSlide(slide.id, {
      ...slide,
      bg: background,
      title,
      subtitle,
      transportOptions: [transport],
    });
  }, [triggerSave]);

  const handleImageUpload = (info, type = "bg") => {
    const file = info.file.originFileObj || info.file;
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (type === "bg") {
          setBackground(reader.result);
        } else {
          setTransport((prev) => ({
            ...prev,
            vanImage: reader.result,
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const update = (field, value) => {
    setTransport((prev) => ({
      ...prev,
      details: { ...prev.details, [field]: value },
    }));
  };

  const updatePoint = (idx, field, value) => {
    const points = [...(transport.points || [])];
    points[idx][field] = value;
    setTransport((prev) => ({ ...prev, points }));
  };

  const addPoint = () => {
    const newPoints = [...(transport.points || []), { icon: "", text: "" }];
    setTransport((prev) => ({ ...prev, points: newPoints }));
  };

  const removePoint = (idx) => {
    const newPoints = transport.points.filter((_, i) => i !== idx);
    setTransport((prev) => ({ ...prev, points: newPoints }));
  };

  return (
    <div className="border rounded-lg p-3 bg-white shadow-sm">
      <Divider />
      <h1 className="font-semibold mb-4 text-lg">Edit Transport Slide</h1>

      <Form layout="vertical">
        <Form.Item className="mb-2" label="Main Title">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="TRANSPORTATION"
          />
        </Form.Item>

        <Form.Item className="mb-2" label="Subtitle">
          <Input
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Not Just A Destination"
          />
        </Form.Item>

        {/* 🔹 Background Image */}
        <Form.Item className="mb-2" label="Background Image">
          <Upload
            accept="image/*"
            beforeUpload={() => false}
            showUploadList={false}
            onChange={(info) => handleImageUpload(info, "bg")}
          >
            <Button type="primary" icon={<UploadOutlined />}>
              Upload Background
            </Button>
          </Upload>
          {background && (
            <img
              src={background}
              alt="Background Preview"
              className="block mt-2 w-full h-32 max-w-xs object-cover rounded-lg border"
            />
          )}
        </Form.Item>

        <Form.Item className="mb-2" label="Van Image">
          <Upload
            accept="image/*"
            beforeUpload={() => false}
            showUploadList={false}
            onChange={(info) => handleImageUpload(info, "vanImage")}
          >
            <Button type="primary" icon={<UploadOutlined />}>
              Upload Van Image
            </Button>
          </Upload>
          {transport.vanImage && (
            <img
              src={transport.vanImage}
              alt="Van Preview"
              className="block mt-2 w-full h-32 max-w-xs object-cover rounded-lg border"
            />
          )}
        </Form.Item>

        <Form.Item className="mb-2" label="Type of Vehicle">
          <Input
            value={transport.details.vehicleType || ""}
            onChange={(e) => update("vehicleType", e.target.value)}
            placeholder="e.g., Van"
          />
        </Form.Item>

        <Form.Item className="mb-2" label="Vehicle Model">
          <Input
            value={transport.details.model || ""}
            onChange={(e) => update("model", e.target.value)}
            placeholder="e.g., KDH Flat Roof"
          />
        </Form.Item>

        <Form.Item className="mb-2" label="Max Passengers">
          <Input
            type="number"
            value={transport.details.maxPassengers}
            onChange={(e) => update("maxPassengers", e.target.value)}
          />
        </Form.Item>

        <Form.Item className="mb-2" label="Max Luggage">
          <Input
            type="number"
            value={transport.details.maxLuggage}
            onChange={(e) => update("maxLuggage", e.target.value)}
          />
        </Form.Item>

        <Form.Item className="mb-2" label="Air Conditioned">
          <Select
            value={transport.details.airConditioned}
            onChange={(value) => update("airConditioned", value)}
            options={[
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ]}
          />
        </Form.Item>

        <h3 className="mt-6 mb-2 font-semibold">Features / Points</h3>
        {transport.points?.map((point, idx) => (
          <div key={idx} className="flex items-center gap-2 mb-2">
            <img
              src={point.icon || defaultPointIcon}
              alt="icon"
              className="w-6 h-6"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = defaultPointIcon;
              }}
            />
            <Input
              placeholder="Feature text"
              value={point.text}
              onChange={(e) => updatePoint(idx, "text", e.target.value)}
              className="w-full"
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => removePoint(idx)}
            />
          </div>
        ))}

        {(transport.points?.length || 0) < 6 && (
          <Button type="dashed" icon={<PlusOutlined />} onClick={addPoint}>
            Add Point
          </Button>
        )}

        <div className="mt-6 flex justify-end">
          <Button danger type="primary" onClick={() => removeSlide(slide.id)}>
            <DeleteOutlined /> Remove Slide
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TransportDetailsEditor;
